import {
    ChangeDetectionStrategy,
    Component,
    Directive,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    NgControl,
    ReactiveFormsModule
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';

import { TablerIconsModule } from 'angular-tabler-icons';
import { filterNilValue } from '@datorama/akita';
import moment from 'moment';

export interface DateParse {
    dateInput: string;
}
export type DateDisplay = DateParse & {
    monthYearLabel?: string;
    dateA11yLabel?: string;
    monthYearA11yLabel?: string;
};
export class CustomDateFormat {
    private _parse: DateParse = {
        dateInput: 'YYYY/MM/DD'
    };
    private _display: DateDisplay = {
        dateInput: 'YYYY/MM/DD',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMM YYYY'
    };

    set parse(parse: DateParse) {
        this._parse = Object.assign({}, this._parse, parse);
    }

    get parse(): DateParse {
        return this._parse;
    }

    set display(display: DateDisplay) {
        this._display = Object.assign({}, this._display, display);
    }

    get display(): DateDisplay {
        return this._display;
    }

    updateDateFormat(parse: DateParse, display?: DateDisplay) {
        this.parse = parse;
        if (!display) {
            display = parse;
        }
        this.display = display;
    }
}

@Directive({
    selector: '[datePickerFormat]',
    providers: [
        {
            provide: MAT_DATE_FORMATS,
            useClass: CustomDateFormat
        }
    ],
    standalone: true
})
export class DatePickerFormatDirective {
    @Input() public configDateParse: DateParse;
    @Input() public configDateDisplay: DateDisplay;

    @Input('datePickerFormat')
    set datePickerFormat(format: string) {
        if (this.configDateParse) {
            this.matDateFormat.updateDateFormat(
                this.configDateParse,
                this.configDateDisplay
            );
        } else {
            this.matDateFormat.updateDateFormat({ dateInput: format });
        }
        // We need this for the first time to tell component change new format
        const value = this.ngControl.value;
        this.ngControl.valueAccessor?.writeValue(value);
    }

    constructor(
        @Inject(MAT_DATE_FORMATS) public matDateFormat: CustomDateFormat,
        @Optional() private ngControl: NgControl
    ) {}
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        ReactiveFormsModule,
        DatePickerFormatDirective,
        TablerIconsModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SbPeriodPickerTempComponent,
            multi: true
        }
    ],
    selector: 'sb-period-picker',
    standalone: true,
    styleUrls: ['./sb-period-picker-temp.component.scss'],
    templateUrl: './sb-period-picker-temp.component.html'
})
export class SbPeriodPickerTempComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    get useUTC(): boolean {
        return this._useUTC;
    }

    /** useUTC
     * @description use UTC dates
     * @type {boolean}
     * @default true
     */
    @Input() set useUTC(value: boolean) {
        this._useUTC = value;
    }
    get label(): string {
        return this._label;
    }

    @Input() set label(value: string) {
        this._label = value;
    }
    constructor() {
        // private _adapter: DateAdapter<any>
        //this._adapter.setLocale(sv);
    }
    unsubscribe$ = new Subject();
    periods = [
        {
            label: 'Month',
            value: 'month'
        },
        {
            label: 'Quarter',
            value: 'quarter'
        },
        {
            label: 'Year',
            value: 'year'
        },
        {
            label: 'Custom',
            value: 'custom'
        }
    ] as const;
    private _label = 'View by';
    private _useUTC = true;
    periodCtrl = new FormControl<(typeof this.periods)[number]['value']>('month');
    dateCtrl = new FormControl<any>(null);
    fromDateCtrl = new FormControl<any>(null);
    toDateCtrl = new FormControl<any>(null);
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(dates: { fromDate: Date; toDate: Date }): void {
        if (dates) {
            this.fromDateCtrl.setValue(dates.fromDate || new Date());
            this.toDateCtrl.setValue(dates.toDate || new Date());
            this.dateCtrl.setValue(dates.fromDate || new Date());
        }
    }
    onChange = (dates) => {};

    onTouched = () => {};

    /** setMonthAndYear - set from and to date to start and end of month for passed date
     * @param date - date to derive year and month from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setMonthAndYear(date: any, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(moment(date).startOf('month'));
        this.toDateCtrl.setValue(moment(date).endOf('month'));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** setQuarterAndYear - set from and to date to start and end of quarter for passed date
     * @param date - date to derive year and quarter from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setQuarterAndYear(date: any, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(moment(date).startOf('quarter'));
        this.toDateCtrl.setValue(moment(date).endOf('quarter'));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** setYear - set from and to date to start and end of year for passed date
     * @param date - date to derive year from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setYear(date: any, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(moment(date).startOf('year'));
        this.toDateCtrl.setValue(moment(date).endOf('year'));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** selectPrevious - select previous period
     * @param period - type of period to select `month | quarter | year`
     */
    selectPrevious(period: Exclude<(typeof this.periods)[number]['value'], 'custom'>) {
        const date = this.dateCtrl.value;
        if (period === 'month') {
            this.setMonthAndYear(moment(date).subtract(1, 'month'));
        } else if (period === 'quarter') {
            this.setQuarterAndYear(moment(date).subtract(1, 'quarter'));
        } else if (period === 'year') {
            this.setYear(moment(date).subtract(1, 'year'));
        }
    }
    /** selectNext - select next period
     * @param period - type of period to select `month | quarter | year`
     */
    selectNext(period: Exclude<(typeof this.periods)[number]['value'], 'custom'>) {
        const date = this.dateCtrl.value;
        if (period === 'month') {
            this.setMonthAndYear(moment(date).add(1, 'month'));
        } else if (period === 'quarter') {
            this.setQuarterAndYear(moment(date).add(1, 'quarter'));
        } else if (period === 'year') {
            this.setYear(moment(date).add(1, 'year'));
        }
    }

    ngOnInit(): void {
        combineLatest([
            this.fromDateCtrl.valueChanges.pipe(
                filterNilValue()
                /*map((fromDate) => startOfDay(fromDate)),
                map((fromDate) =>
                    this.useUTC ? convertLocalDateToUTC(fromDate) : fromDate
                )*/
            ),
            this.toDateCtrl.valueChanges.pipe(
                filterNilValue()
                /*map((toDate) => endOfDay(toDate)),
                map((toDate) => (this.useUTC ? convertLocalDateToUTC(toDate) : toDate))*/
            )
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([fromDate, toDate]) => {
                this.onChange({ fromDate, toDate });
            });
        this.periodCtrl.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((period) => {
                const date = this.dateCtrl.value;
                if (period === 'month') {
                    this.setMonthAndYear(date);
                } else if (period === 'quarter') {
                    this.setQuarterAndYear(date);
                } else if (period === 'year') {
                    this.setYear(date);
                }
            });
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
